import Vue from 'vue'
import vuetify from '@/plugins/vuetify'
import ChunkErrorModal from '@/components/ChunkErrorModal.vue'
import {
	CHUNK_ERROR_DECISION,
	getChunkErrorHandlingDecision,
} from '@/utils/chunkErrorDecision'

const RELOAD_COUNT_KEY = 'chunkReloadCount'
const MAX_RELOAD_ATTEMPTS = 3

let chunkErrorModalInstance = null
let isModalShowing = false
let isHandlingChunkError = false
let permanentlyExhausted = false
let pendingNavigationTarget = null

export const setPendingNavigationTarget = fullPath => {
	pendingNavigationTarget = fullPath
}

export const clearPendingNavigationTarget = () => {
	pendingNavigationTarget = null
}

export const cleanupChunkErrorModal = () => {
	if (chunkErrorModalInstance) {
		try {
			chunkErrorModalInstance.$off()
			chunkErrorModalInstance.$destroy()
		} catch (err) {}
		chunkErrorModalInstance = null
	}
	isModalShowing = false
	isHandlingChunkError = false
}

const destroyModalInstance = () => {
	if (!chunkErrorModalInstance) return
	try {
		chunkErrorModalInstance.$off()
		chunkErrorModalInstance.$destroy()
		if (chunkErrorModalInstance.$el && chunkErrorModalInstance.$el.parentNode) {
			chunkErrorModalInstance.$el.parentNode.removeChild(
				chunkErrorModalInstance.$el,
			)
		}
	} catch (e) {
		console.warn('清理彈窗實例時出錯:', e)
	}
	chunkErrorModalInstance = null
}

const showChunkErrorModal = mode => {
	// 防止重複顯示彈窗
	if (isModalShowing) {
		return Promise.resolve(false)
	}

	return new Promise(resolve => {
		isModalShowing = true
		destroyModalInstance()

		const ChunkErrorModalConstructor = Vue.extend(ChunkErrorModal)
		chunkErrorModalInstance = new ChunkErrorModalConstructor({
			vuetify,
			propsData: {
				value: true,
				mode,
			},
		})

		const cleanup = () => {
			destroyModalInstance()
			isModalShowing = false
		}

		chunkErrorModalInstance.$on('onConfirm', () => {
			cleanup()
			resolve(true)
		})

		// 彈窗自身渲染若也壞掉（罕見），仍要回收避免卡在 isModalShowing
		chunkErrorModalInstance.$on('hook:errorCaptured', error => {
			console.error('彈窗組件錯誤:', error)
			cleanup()
			resolve(false)
		})

		try {
			chunkErrorModalInstance.$mount()
			document.body.appendChild(chunkErrorModalInstance.$el)
		} catch (e) {
			console.error('創建彈窗時出錯:', e)
			cleanup()
			resolve(false)
		}
	})
}

const currentFullPath = () =>
	window.location.pathname + window.location.search + window.location.hash

export const handleChunkLoadError = error => {
	const decision = getChunkErrorHandlingDecision(error, {
		isHandlingChunkError,
		permanentlyExhausted,
	})
	if (decision === CHUNK_ERROR_DECISION.IGNORE) {
		return false
	}
	// 到達 reload 上限後不再處理，或同一次 burst 已有 listener 正在處理時，不重複啟動流程
	if (decision === CHUNK_ERROR_DECISION.ALREADY_HANDLED) {
		return true
	}

	isHandlingChunkError = true

	// 用 sessionStorage 計數防止新版本本身也壞掉時陷入無限刷新
	// sessionStorage 是 per-tab，關掉分頁重開即重置
	const reloadCount = parseInt(
		sessionStorage.getItem(RELOAD_COUNT_KEY) || '0',
		10,
	)

	console.warn(
		`檢測到資源載入錯誤 (第 ${reloadCount + 1}/${MAX_RELOAD_ATTEMPTS} 次)`,
		error,
	)

	if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
		console.error(
			`ChunkLoadError 重載次數已達上限 (${MAX_RELOAD_ATTEMPTS} 次)，停止自動重載`,
			error,
		)
		// 顯示 exhausted 版本的彈窗（按鈕只關閉不 reload），並設旗標阻擋後續錯誤
		// 想要再嘗試，使用者需手動關閉分頁重開（會清掉 sessionStorage 計數）
		permanentlyExhausted = true
		showChunkErrorModal('exhausted').finally(() => {
			isHandlingChunkError = false
		})
		return true
	}

	sessionStorage.setItem(RELOAD_COUNT_KEY, String(reloadCount + 1))

	showChunkErrorModal('reload')
		.then(shouldReload => {
			if (!shouldReload) return
			// 若有 pending 的 navigation 目標就直接跳過去（讓新版載入後直接到目標頁）
			// 否則只 reload 當前 URL
			const target = pendingNavigationTarget
			if (target && target !== currentFullPath()) {
				window.location.href = target
			} else {
				window.location.reload()
			}
		})
		.finally(() => {
			// 延遲重置處理狀態，避免同一次 chunk 載入失敗的多重觸發又彈一次窗
			setTimeout(() => {
				isHandlingChunkError = false
			}, 1000)
		})

	return true
}
